import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AttachmentType } from '@app/core/models/attachment-type.model';
import { AttachmentModel } from '@app/core/models/attachment.model';
import { InvoiceService } from '@app/core/services/invoice.service';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { ModalHelper } from '@app/shared/models/modal-helper.model';
import * as _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
})
export class PdfViewerComponent implements OnInit, OnChanges, OnDestroy {
  private unsubscribe$: Subject<boolean> = new Subject<boolean>();

  isLoadingFirst: boolean = true;
  isErrorFirst: boolean = true;
  selectedAttachmentFirst: AttachmentModel | null;

  isLoadingSecond: boolean = false;
  isErrorSecond: boolean = false;
  selectedAttachmentSecond: AttachmentModel | null;

  @Input() fileName = (): string => '';
  @Input() firstViewerDocumentSelector = (
    attachmentType: AttachmentType
  ): boolean => false;
  @Input() secondViewerDocumentSelector = (
    attachmentType: AttachmentType
  ): boolean => false;
  @Input() canDeleteQuoteAttachment: boolean = false;
  @Input() canDeleteOrderAttachment: boolean = false;
  @Input() attachments: Array<AttachmentModel>;
  @Input() objectId: string;

  @ViewChild('pdfViewerFirst') pdfViewerFirst: any;
  @ViewChild('pdfViewerSecond') pdfViewerSecond: any;

  constructor(
    public dialog: MatDialog,
    private invoiceService: InvoiceService
  ) {}

  ngOnInit(): void {
    this.selectedAttachmentFirst = _.head(
      this.attachments.filter((a) => this.firstViewerDocumentSelector(a.TypeId))
    );
    if (this.selectedAttachmentFirst) {
      this.showAttachmentFirst(this.selectedAttachmentFirst);
    }
    if (_.size(this.attachments) > 1) {
      this.selectedAttachmentSecond = _.head(
        this.attachments.filter((a) =>
          this.secondViewerDocumentSelector(a.TypeId)
        )
      );
      if (this.selectedAttachmentSecond) {
        this.showAttachmentSecond(this.selectedAttachmentSecond);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['attachments']) {
      const attachmentChanges: SimpleChange = changes['attachments'];
      if (!attachmentChanges.firstChange) {
        this.selectedAttachmentFirst = null;
        this.selectedAttachmentSecond = null;
      }
    }
  }

  deleteAttachment(attachment: AttachmentModel): void {
    const modalConfig = new ModalHelper().getConfirmationConfig(
      `Êtes-vous sûr de vouloir supprimer <strong>'${attachment.Name}'</strong>, de type ${attachment.TypeName} ?`
    );
    const dialog = this.dialog.open(ModalComponent, modalConfig);

    dialog
      .afterClosed()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isConfirmed: boolean) => {
        if (isConfirmed) {
          this.deleteAttachmentConfirmation(attachment);
        }
      });
  }

  deleteAttachmentConfirmation(attachment: AttachmentModel): void {
    this.removeAttachment(attachment)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((_) => {
        if (this.selectedAttachmentFirst?.Id === attachment.Id) {
          this.selectedAttachmentFirst = null;
        }

        if (this.selectedAttachmentSecond?.Id === attachment.Id) {
          this.selectedAttachmentSecond = null;
        }

        this.attachments = this.attachments.filter(
          (a: AttachmentModel) => a.Id != attachment.Id
        );
      });
  }

  clickAttachment(attachment: AttachmentModel): void {
    if (this.selectedAttachmentFirst?.Id === attachment.Id) {
      if (this.selectedAttachmentSecond) {
        this.selectedAttachmentFirst = this.selectedAttachmentSecond;
        this.pdfViewerFirst.pdfSrc = this.pdfViewerSecond.pdfSrc;
        this.pdfViewerFirst.refresh();
        this.selectedAttachmentSecond = undefined;
        this.pdfViewerSecond.pdfSrc = null;
      } else {
        this.selectedAttachmentFirst = undefined;
        this.pdfViewerFirst.pdfSrc = null;
      }
      return;
    }

    if (this.selectedAttachmentSecond?.Id === attachment.Id) {
      this.selectedAttachmentSecond = undefined;
      this.pdfViewerSecond.pdfSrc = null;
      return;
    }

    if (!this.selectedAttachmentFirst) {
      this.showAttachmentFirst(attachment);
      return;
    }

    let selectedAttachment: AttachmentModel = _.head(
      this.attachments.filter((a: AttachmentModel) => a.Id === attachment.Id)
    );
    if (selectedAttachment.TypeId === AttachmentType.INVOICE) {
      this.selectedAttachmentSecond = this.selectedAttachmentFirst;
      this.pdfViewerSecond.pdfSrc = this.pdfViewerFirst.pdfSrc;
      this.pdfViewerSecond.refresh();
      this.selectedAttachmentFirst = null;
      this.showAttachmentFirst(attachment);
      return;
    }

    if (this.selectedAttachmentSecond) {
      selectedAttachment = _.head(
        this.attachments.filter(
          (a: AttachmentModel) => a.Id === this.selectedAttachmentSecond.Id
        )
      );
      if (selectedAttachment.TypeId === AttachmentType.INVOICE) {
        this.selectedAttachmentFirst = this.selectedAttachmentSecond;
        this.pdfViewerFirst.pdfSrc = this.pdfViewerSecond.pdfSrc;
        this.pdfViewerFirst.refresh();
        this.selectedAttachmentSecond = null;
      }
    }
    this.showAttachmentSecond(attachment);
  }

  showAttachmentFirst(attachment: AttachmentModel): void {
    if (attachment.Id === this.selectedAttachmentSecond?.Id) {
      return;
    }
    let selectedAttachment: AttachmentModel = _.head(
      this.attachments.filter((a: AttachmentModel) => a.Id === attachment.Id)
    );

    this.selectedAttachmentFirst = attachment;
    this.isLoadingFirst = true;
    this.isErrorFirst = false;
    this.getAttachment(attachment)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: Blob) => {
          this.isLoadingFirst = false;
          this.pdfViewerFirst.pdfSrc = data;
          if (selectedAttachment.TypeId === AttachmentType.INVOICE) {
            this.pdfViewerFirst.downloadFileName = this.fileName();
          } else {
            this.pdfViewerFirst.downloadFileName = selectedAttachment.Name;
          }
          this.pdfViewerFirst.refresh();
        },
        (_) => {
          this.isLoadingFirst = false;
          this.isErrorFirst = true;
        }
      );
  }

  showAttachmentSecond(attachment: AttachmentModel): void {
    if (attachment.Id === this.selectedAttachmentFirst?.Id) {
      return;
    }
    let selectedAttachment: AttachmentModel = _.head(
      this.attachments.filter((a: AttachmentModel) => a.Id === attachment.Id)
    );

    this.selectedAttachmentSecond = attachment;
    this.isLoadingSecond = true;
    this.isErrorSecond = false;
    this.getAttachment(attachment)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: Blob) => {
          this.isLoadingSecond = false;
          this.pdfViewerSecond.pdfSrc = data;
          if (selectedAttachment.TypeId === AttachmentType.INVOICE) {
            this.pdfViewerSecond.downloadFileName = this.fileName();
          } else {
            this.pdfViewerSecond.downloadFileName = selectedAttachment.Name;
          }
          this.pdfViewerSecond.refresh();
        },
        (_) => {
          this.isLoadingSecond = false;
          this.isErrorSecond = true;
        }
      );
  }

  getAttachment(attachment: AttachmentModel): Observable<Blob> {
    if (attachment.InvoiceId) {
      return this.invoiceService.getAttachment(
        attachment.InvoiceId,
        attachment.Id
      );
    }
    throw new Error('No invoice found with this attachment.');
  }

  removeAttachment(attachment: AttachmentModel): Observable<any> {
    if (attachment.InvoiceId) {
      return this.invoiceService.removeAttachment(
        attachment.InvoiceId,
        attachment.Id
      );
    }

    throw new Error('No invoice found with this attachment.');
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
  }
}
